import type { ReadinessResponse } from '@lousada/shared';
import type { ReadinessOutcome } from './status';

/**
 * Same-origin API path.
 *
 * Never a full URL: in development Vite proxies `/api`, in Compose the web
 * container's Caddy does, and in production https://mobilidade-lousada.jsd-lousada.pt serves
 * both the application and the API from one origin.
 */
const READINESS_PATH = '/api/health/ready';

/** Guards against a proxy returning HTML (or anything else) instead of the contract. */
function isReadinessResponse(value: unknown): value is ReadinessResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ReadinessResponse>;
  return (
    typeof candidate.status === 'string' &&
    typeof candidate.api === 'object' &&
    candidate.api !== null &&
    typeof candidate.database === 'object' &&
    candidate.database !== null &&
    typeof candidate.routing === 'object' &&
    candidate.routing !== null
  );
}

/**
 * Fetches readiness.
 *
 * A 503 is a valid, meaningful answer (the API is up, and telling us a
 * dependency is not), so its body is used rather than treated as a transport
 * failure. Only a genuinely unusable response becomes `unreachable`.
 */
export async function fetchReadiness(signal?: AbortSignal): Promise<ReadinessOutcome> {
  let response: Response;
  try {
    response = await fetch(READINESS_PATH, {
      headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    return {
      kind: 'unreachable',
      detail: error instanceof Error ? error.message : 'O pedido falhou.',
    };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return {
      kind: 'unreachable',
      detail: `O serviço respondeu ${response.status} sem dados válidos.`,
    };
  }

  if (!isReadinessResponse(payload)) {
    return {
      kind: 'unreachable',
      detail: `O serviço respondeu ${response.status} com uma resposta inesperada.`,
    };
  }

  return { kind: 'loaded', readiness: payload };
}
